/**
 * Session Management System
 * Handles user session security, timeouts, and validation
 */

import { secureStorage, sanitizeErrorMessage } from './security';
import { SECURITY_CONFIG } from '../config/security';

class SessionManager {
  constructor() {
    this.sessionKey = 'chirpx_user_session';
    this.lastActivityKey = 'chirpx_last_activity';
    this.sessionTimeout = SECURITY_CONFIG.SESSION.TIMEOUT;
    this.refreshThreshold = SECURITY_CONFIG.SESSION.REFRESH_THRESHOLD;
    this.maxConcurrentSessions = SECURITY_CONFIG.SESSION.MAX_CONCURRENT_SESSIONS;
    
    // Initialize session monitoring
    this.initializeSessionMonitoring();
  }

  /**
   * Create a new user session
   */
  createSession(walletAddress, sessionData = {}) {
    try {
      const session = {
        walletAddress,
        sessionId: this.generateSessionId(),
        createdAt: Date.now(),
        lastActivity: Date.now(),
        isActive: true,
        ...sessionData
      };

      // Store session securely
      secureStorage.setItem(this.sessionKey, session);
      secureStorage.setItem(this.lastActivityKey, Date.now());

      console.log('✅ Session created for wallet:', walletAddress.slice(0, 8) + '...');
      return session;
    } catch (error) {
      console.error('❌ Failed to create session:', sanitizeErrorMessage(error));
      return null;
    }
  }

  /**
   * Get current active session
   */
  getCurrentSession() {
    try {
      const session = secureStorage.getItem(this.sessionKey);
      if (!session) return null;

      // Check if session is expired
      if (this.isSessionExpired(session)) {
        this.destroySession();
        return null;
      }

      return session;
    } catch (error) {
      console.error('❌ Failed to get session:', sanitizeErrorMessage(error));
      return null;
    }
  }

  /**
   * Update session activity timestamp
   */
  updateActivity() {
    try {
      const session = this.getCurrentSession();
      if (!session) return false;

      session.lastActivity = Date.now();
      secureStorage.setItem(this.sessionKey, session);
      secureStorage.setItem(this.lastActivityKey, Date.now());

      return true;
    } catch (error) {
      console.error('❌ Failed to update activity:', sanitizeErrorMessage(error));
      return false;
    }
  }

  /**
   * Check if session is expired
   */
  isSessionExpired(session = null) {
    try {
      const currentSession = session || this.getCurrentSession();
      if (!currentSession) return true;

      const now = Date.now();
      const sessionAge = now - currentSession.createdAt;
      const inactivityTime = now - currentSession.lastActivity;

      return sessionAge > this.sessionTimeout || inactivityTime > this.sessionTimeout;
    } catch (error) {
      console.error('❌ Failed to check session expiry:', sanitizeErrorMessage(error));
      return true;
    }
  }

  /**
   * Destroy current session
   */
  destroySession() {
    try {
      const session = secureStorage.getItem(this.sessionKey);
      if (session) {
        console.log('🗑️ Session destroyed for wallet:', session.walletAddress?.slice(0, 8) + '...');
      }

      secureStorage.removeItem(this.sessionKey);
      secureStorage.removeItem(this.lastActivityKey);
      
      return true;
    } catch (error) {
      console.error('❌ Failed to destroy session:', sanitizeErrorMessage(error));
      return false;
    }
  }

  /**
   * Validate session security
   */
  validateSession(walletAddress) {
    try {
      const session = this.getCurrentSession();
      if (!session) return { valid: false, reason: 'No active session' };

      if (session.walletAddress !== walletAddress) {
        return { valid: false, reason: 'Wallet address mismatch' };
      }

      if (this.isSessionExpired(session)) {
        this.destroySession();
        return { valid: false, reason: 'Session expired' };
      }

      return { valid: true, session };
    } catch (error) {
      console.error('❌ Session validation failed:', sanitizeErrorMessage(error));
      return { valid: false, reason: 'Validation error' };
    }
  }

  /**
   * Initialize session monitoring
   */
  initializeSessionMonitoring() {
    // Check session every 30 seconds
    setInterval(() => {
      this.checkSessionHealth();
    }, 30000);

    // Update activity on user interactions
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, () => {
        this.updateActivity();
      }, { passive: true });
    });

    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkSessionHealth();
      }
    });
  }

  /**
   * Check session health and cleanup if needed
   */
  checkSessionHealth() {
    try {
      const session = this.getCurrentSession();
      if (session && this.isSessionExpired(session)) {
        console.warn('⚠️ Session expired, cleaning up...');
        this.destroySession();
        
        // Dispatch session expired event
        window.dispatchEvent(new CustomEvent('sessionExpired', {
          detail: { reason: 'timeout' }
        }));
      }
    } catch (error) {
      console.error('❌ Session health check failed:', sanitizeErrorMessage(error));
    }
  }

  /**
   * Generate secure session ID
   */
  generateSessionId() {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2);
    return `${timestamp}-${randomPart}`;
  }

  /**
   * Get session statistics for monitoring
   */
  getSessionStats() {
    try {
      const session = this.getCurrentSession();
      if (!session) return null;

      const now = Date.now();
      return {
        sessionAge: now - session.createdAt,
        inactivityTime: now - session.lastActivity,
        isExpired: this.isSessionExpired(session),
        walletAddress: session.walletAddress?.slice(0, 8) + '...',
        sessionId: session.sessionId
      };
    } catch (error) {
      console.error('❌ Failed to get session stats:', sanitizeErrorMessage(error));
      return null;
    }
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;
export { SessionManager };
